import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Switch from 'react-switch'
import { CAMERA_SELECTED_FULL_RGB_COLORS, ANALYTIC_ZONE_OPACITIES,
  COLORS, CAMERA_SELECTED_COLORS } from 'helpers/globals'
import { updateUserSettingsAction } from 'actions/settingsActions'
import { updateUiAction } from 'actions/uiActions'
import clamp from 'ramda/src/clamp'
import tippy from 'tippy.js'
import drawCanvas from './drawCanvas'

const iconPath = 'm 2 .6 c -1 0 -1.3 0.7 -0.9 1.5 l 1.4 2.4 c 0.5 0.8 1.7 1.5 2.6 1.5 h 2.1 c 1 0 2.1 -0.7 2.6 -1.5 l 1.4 -2.4 c 0.5 -0.8 0.1 -1.5 -0.9 -1.5 l -8.3 0 l 0 0 z m -0.9 23 c 0 1 0.8 1.7 1.7 1.7 h 6.8 c 1 0 1.7 -0.8 1.7 -1.7 v -14.4 c 0 -1 -0.8 -1.7 -1.7 -1.7 h -6.8 c -1 0 -1.7 0.8 -1.7 1.7 v 14.4 l 0 0 z'
export const ANGLE = 50
const RESOLUTION = 1920

const mapStateToProps = state => {
  const { userSettings: { ppfZoneBorders, ppfZoneColors, ppfZoneColorOverride }, ui: { isImperial } } = state
  return {
    ppfZoneBorders,
    ppfZoneColors,
    ppfZoneColorOverride,
    isImperial
  }
}

class AnalyticZonesSelector extends React.Component {
  constructor(props) {
    super(props)
    let borders = this.props.ppfZoneBorders.slice(1)

    this.isImperial = this.props.isImperial
    if (this.isImperial) borders = borders.map(v => clamp(1, 300, Math.round(v / 3.28)))

    const { widths, distances } = this.getDistances(borders)
    this.canvas = null
    this.ctx = null
    this.messageTimer = null
    this.tooltip = null
    this.state = {
      borders,
      widths,
      distances,
      colors: this.props.ppfZoneColors.slice(),
      override: this.props.ppfZoneColorOverride,
      errorText: '',
      invalidInput: null
    }
    this.animationFrame = null
  }

  componentDidMount = _ => {
    const { distances, override, colors } = this.state
    this.canvas = this.refs.canvas
    this.ctx = this.canvas.getContext('2d')

    drawCanvas(distances, override, colors.map((c, j) => this.getColor(c, j, override)), this.canvas, this.ctx)

    window.addEventListener('resize', this.updateHeight)

    tippy(this.refs.default, { duration: 0, theme: 'topbar', placement: 'top', onShow: instance => this.tooltip = instance })
  }

  componentWillUnmount = _ => {
    const { messageTimer, animationFrame, updateHeight, tooltip } = this

    clearTimeout(messageTimer)
    cancelAnimationFrame(animationFrame)
    window.removeEventListener('resize', updateHeight)

    tooltip && tooltip.hide()
  }

  updateHeight = _ => {
    const { state, animationFrame, getColor, canvas, ctx } = this

    cancelAnimationFrame(animationFrame)
    this.animationFrame = requestAnimationFrame(_ => drawCanvas(state.distances, state.override, state.colors.map((c, j) => getColor(c, j, state.override)), canvas, ctx))
  }

  handleChange = (event, i) => {
    if (this.state.invalidInput !== null && this.state.invalidInput !== i) return
    clearTimeout(this.messageTimer)

    const previousDistances = this.state.distances
    const upperLimit = this.isImperial ? 300 : 999
    const unit = this.isImperial ? 'PPF' : 'PPM'

    if (!Number.isInteger(Number.parseInt(event.target.value)) || event.target.value < 1 || (event.target.value > upperLimit && i === 0)) {
      this.setState({ errorText: `Zone ${i + 1} / ${i + 2} boundary must be between 1 and ${upperLimit} ${unit}`, invalidInput: i })
      return
    }

    let errorText = ''
    const borders = [...this.state.borders]
    borders[i] = Number.parseInt(event.target.value)

    if (borders[0] <= borders[1]) {
      errorText = i === 0 ? `Zone 1 / 2 boundary must be greater than zone 2 / 3 boundary (${borders[1]} ${unit})` : `Zone 2 / 3 boundary must be less than zone 1 / 2 boundary (${borders[0]} ${unit})`
    } else if (borders[1] <= borders[2]) {
      errorText = i === 1 ? `Zone 2 / 3 boundary must be greater than zone 3 / 4 boundary (${borders[2]} ${unit})` : `Zone 3 / 4 boundary must be less than zone 2 / 3 boundary (${borders[1]} ${unit})`
    }

    if (errorText === '') {
      const { distances, widths } = this.getDistances(borders)
      this.setState({ borders, widths, distances, invalidInput: null }, _ => this.animateDisplay({ distances, previousDistances, distanceIndex: i, colors: this.state.colors, startTime: Date.now() }))
      this.messageTimer = setTimeout(_ => this.setState({ errorText }), 200)
    } else {
      this.setState({ errorText, invalidInput: i })
    }
  }

  handleColorChange = (color, i) => {
    if (!this.state.override) return
    const previousColors = this.state.colors
    const colors = this.state.colors.slice()
    colors[i] = color
    this.setState({ colors }, _ => this.animateDisplay({ colors, previousColors, colorIndex: i, distances: this.state.distances, startTime: Date.now() }))
  }

  handleOverrideChange = override => {
    const previousColors = this.state.colors
    this.setState({ override }, _ => this.animateDisplay({ colors: this.state.colors, previousColors, colorIndex: 'all', distances: this.state.distances, startTime: Date.now() }))
  }

  getColor = (key, i, override, skipOpacity = false) => {
    const color = CAMERA_SELECTED_FULL_RGB_COLORS[override ? key : 'blue']
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${skipOpacity ? 1 : ANALYTIC_ZONE_OPACITIES[i]})`
  }

  savePPFZoneBorders = _ => {
    if (this.state.invalidInput !== null) return
    let borders = this.state.borders.slice(0)

    if (this.isImperial) borders = borders.map(v => Math.round(v * 3.28))
    borders.unshift(borders[0])

    this.props.updateUserSettingsAction({ ppfZoneBorders: borders, ppfZoneColors: this.state.colors.slice(), ppfZoneColorOverride: this.state.override })
    this.props.updateUiAction({ modal: null })
  }

  defaultPPFZoneBorders = _ => {
    clearTimeout(this.messageTimer)
    const borders = this.isImperial ? [100, 50, 10] : [328, 164, 32]
    const previousDistances = this.state.distances
    const previousColors = this.state.colors
    const { distances, widths } = this.getDistances(borders)

    this.setState({ borders, override: false, colors: ['blue', 'blue', 'blue', 'blue'], errorText: '', widths, distances, invalidInput: null },
      _ => this.animateDisplay({ distances, previousDistances, distanceIndex: 2, colors: this.state.colors, previousColors, colorIndex: 'all', startTime: Date.now() }))
    this.refs.input0.value = borders[0]
    this.refs.input1.value = borders[1]
    this.refs.input2.value = borders[2]
  }

  getDistances = borders => {
    const distances = borders.map(b => RESOLUTION * 180 / (b * ANGLE * Math.PI))
    const distance = 1.075 * distances[distances.length - 1]

    let widths = []
    widths.push(100 * distances[0] / distance)
    for (let i = 1; i < distances.length; i++) {
      widths.push(100 * (distances[i] - distances[i - 1]) / distance)
    }

    return { distances, widths }
  }

  animateDisplay = ({ previousDistances, distances, distanceIndex, previousColors, colors, colorIndex, startTime }) => {
    cancelAnimationFrame(this.animationFrame)
    const blue = CAMERA_SELECTED_FULL_RGB_COLORS.blue
    const mappedColors = colors.map((c, j) => this.getColor(c, j, this.state.override))

    const transformColor = (color, i, mult) => {
      if (colorIndex !== 'all' && colorIndex !== i) return this.getColor(color, i, this.state.override)
      const oldColor = colorIndex === 'all' && this.state.override ? blue : CAMERA_SELECTED_FULL_RGB_COLORS[previousColors[i]]
      const newColor = colorIndex === 'all' && !this.state.override ? blue : CAMERA_SELECTED_FULL_RGB_COLORS[color]

      const convertedColor = {
        r: (newColor.r - oldColor.r) * mult + oldColor.r,
        g: (newColor.g - oldColor.g) * mult + oldColor.g,
        b: (newColor.b - oldColor.b) * mult + oldColor.b
      }

      return `rgba(${convertedColor.r}, ${convertedColor.g}, ${convertedColor.b}, ${ANALYTIC_ZONE_OPACITIES[i]})`
    }

    const transformDistance = (distance, i, mult) =>
      distanceIndex === 2 && i > 1 ? previousDistances[i] : (distances[i] - previousDistances[i]) * mult + previousDistances[i]

    const animate = _ => {
      const frac = (Date.now() - startTime) / 200
      const mult = Math.max(0, Math.min(frac, 1))

      const newColors = colorIndex !== undefined ? colors.map((color, i) => transformColor(color, i, mult)) : mappedColors
      const newDistances = distanceIndex !== undefined ? distances.map((distance, i) => transformDistance(distance, i, mult)) : distances

      drawCanvas(newDistances, this.state.override, newColors, this.canvas, this.ctx)

      if (frac < 1) this.animationFrame = requestAnimationFrame(animate)
    }

    this.animationFrame = requestAnimationFrame(animate)
  }

  render() {
    const { handleColorChange, isImperial, defaultPPFZoneBorders, getColor, props: { classes },
      state: { colors, override, widths, borders, errorText, invalidInput } } = this

    return (
      <div className={classes.container}>
        <div className={classes.warning}>
          <FontAwesomeIcon icon='info-circle' /> Affects all projects
        </div>
        <div className={classes.canvasContainer}>
          <svg viewBox='0 0 12.4 26' className={classes.cameraIcon}>
            <path d={iconPath} stroke={CAMERA_SELECTED_COLORS.blue} fill='none' strokeWidth='2' />
          </svg>
          <canvas ref='canvas' />
        </div>
        <span style={{ fontSize: '1.4rem' }}>{isImperial ? 'PPFs' : 'PPMs'} where zones change</span>
        <div className={classes.sliderContainer}>
          { [0, 1, 2].map(i => (
              <div className={classes.inputContainer} style={{ flex: `1 1 ${widths[i]}%` }} key={'inputContainer' + i}>
                <div className={classes.zones} style={{ background: getColor(colors[i], i, override) }}>
                  {`Zone ${i + 1}`}
                  <div className={classes.ppfInputBox}>
                    <div className={classes.ppfInputWrapper + (invalidInput !== null && invalidInput === i ? ' invalid' : '')}>
                      <input
                        type='number'
                        defaultValue={borders[i]}
                        onChange={event => this.handleChange(event, i)}
                        ref={'input' + i}
                        disabled={invalidInput !== null && invalidInput !== i}
                      />
                      <span>{isImperial ? 'PPF' : 'PPM'}</span>
                    </div>
                    <div className={classes.arrowDown} />
                  </div>
                </div>
              </div>
            ))
          }
          <div className={classes.inputContainer} style={{ flex: '0 0 10%' }}>
            <div className={classes.zones} style={{ background: getColor(colors[3], 3, override) }}>Zone 4</div>
          </div>
          <div className={classes.ruler}>
            { [...Array(10)].map((_, i) => <div className={classes.mark} style={{ left: `${10 * i}%` }} key={i} />) }
          </div>
        </div>
        <div className={classes.superlativeWrapper}>
          <span>Best</span>
          <span>Worst</span>
        </div>
        <div className={classes.colorRow}>
          <span style={{ marginRight: '1rem' }}>Customize zone colors</span>
          <Switch
            checked={override}
            onChange={this.handleOverrideChange}
            onColor='#5CA5E4'
            onHandleColor={COLORS.primary}
            handleDiameter={25}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)'
            activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)'
            height={17}
            width={40}
            className='react-switch'
          />
          <div className={classes.colorWrapper} style={{ filter: override ? 'none' : 'grayscale(1)' }}>
            { colors.map((color, i) => (
                <div className={classes.colorPicker} key={'colorPicker' + i}>
                  <span style={{ background: getColor(colors[i], i, override, true) }}>
                    { `Zone ${i + 1}` }
                  </span>
                  <div className={classes.colorsContainer}>
                    { Object.keys(CAMERA_SELECTED_FULL_RGB_COLORS).map(colorOption =>
                        <div
                          onClick={_ => handleColorChange(colorOption, i)}
                          className={classes.color + (override ? '' : ' disabled')}
                          style={{ background: getColor(colorOption, i, true) }}
                          key={'colorChoice' + colorOption}
                        >
                          { override && colors[i] === colorOption &&
                            <FontAwesomeIcon icon='check' className={classes.checkIcon} />
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        <div style={{ display: 'flex' }}>
          <div className={classes.saveButton + (invalidInput !== null ? ' disabled' : '')} onClick={this.savePPFZoneBorders}>
            Save {isImperial ? 'PPF' : 'PPM'} Zones
          </div>
          <div className={classes.errorText}>
            <span className={invalidInput !== null ? classes.errorTextFadeIn : classes.errorTextFadeOut}>
              <FontAwesomeIcon icon='exclamation-triangle' className={classes.errorIcon} />
              {errorText}
            </span>
          </div>
          <div
            onClick={defaultPPFZoneBorders}
            title={`Resets colors and sets boundaries to ${isImperial ? '100, 50, and 10' : '328, 164, and 32'}`}
            ref='default'
            className={classes.defaultButton}
          >
            Default
          </div>
        </div>
      </div>
    )
  }
}

export const AnalyticZonesSelectorConfig = { width: '900px' }
export default connect(mapStateToProps, { updateUserSettingsAction, updateUiAction })(injectSheet(style)(AnalyticZonesSelector))



// WEBPACK FOOTER //
// ./src/components/AnalyticZonesSelector/index.js